import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { resetPasswordSchema } from "../../../utils/resetPasswordSchema";
import { useVerifyPhoneMutation, useFindPwByPhoneMutation } from "../../../api/resetPasswordAPI";
import {useAuth} from "../AuthContext";
import { toast } from "../../../utils/toast";

const FindPwPage = () => {
    // 아이디 찾기 요청 상태 관리 (idle, pending, success, failure)
    const [isLoading, setIsLoading] = useState(false);

    // 인증번호 요청 상태 관리 (idle, sending, success, failure)
    const [verificationStatus, setVerificationStatus] = useState("idle");

    // 인증번호 요청 결과 메시지 상태 관리
    const [verificationMessage, setVerificationMessage] = useState("");

    // 남은 시간 (초 단위) 상태 관리
    const [remainingTime, setRemainingTime] = useState(180); // 3 minutes in seconds
    const navigate = useNavigate();

    // 휴대폰 인증번호 요청 mutation
    const verifyPhoneMutation = useVerifyPhoneMutation();
    const findPwMutation = useFindPwByPhoneMutation();

    const { allowPasswordReset } = useAuth();

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm({
        resolver: zodResolver(resetPasswordSchema),
        mode: 'onChange',
    });

    const loginId = watch('loginId');
    const name = watch('name');
    const phoneNumber = watch('phoneNumber');
    const authNumber = watch('authNumber');

    useEffect(() => {
        let timer;
        if (verificationStatus === 'sent' && remainingTime > 0) {
            timer = setInterval(() => {
                setRemainingTime((prevTime) => prevTime - 1);
            }, 1000);
        } else if (remainingTime === 0) {
            setVerificationStatus('idle');
            setVerificationMessage('인증번호 입력 시간이 초과되었습니다. 다시 시도해주세요.');
        }
        return () => clearInterval(timer);
    }, [verificationStatus, remainingTime]);

    const handlePhoneVerification = async () => {
        try {
            setVerificationStatus('sending');
            setVerificationMessage('');

            // await verifyPhone(username, phone);
            await verifyPhoneMutation.mutateAsync({ phoneNumber: phoneNumber });

            setVerificationStatus('sent');
            setVerificationMessage('인증번호가 발송되었습니다. 휴대폰을 확인해주세요.');
            setRemainingTime(180);
        } catch (error) {
            setVerificationStatus('failed');
            // setVerificationMessage(error.message);
            setVerificationMessage('전화번호 인증 요청에 실패했습니다.');
        }
    };

    const onSubmit = async (formData) => {
        // 아이디 찾기 버튼 클릭 시 로딩 상태로 변경
        setIsLoading(true);

        try {
            const signupData = {
                loginId: formData.loginId,
                name: formData.name,
                phoneNumber : formData.phoneNumber,
                authNumber: formData.authNumber,
                timeStamp: Date.now(),
            };

            let result = await findPwMutation.mutateAsync(signupData);
            if (result["message"] === "비밀번호 찾기 성공"){
                allowPasswordReset();   // 비밀번호 변경 사이트 접속 가능 상태로 변경
                toast("PW 찾기 성공");
                navigate("/find-pw/reset-password", { state: { result: result["data"]["loginId"] } });
            }
        } catch (error) {
            console.error("PW 찾기 실패:", error);
            throw error;
        } finally {
            // 아이디 찾기 요청 완료 시 로딩 상태 해제
            setIsLoading(false);
        }
    };

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    };

    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-white px-4 mt-[170px]">
            <div className="w-full max-w-md space-y-8">
                <h2 className="text-2xl font-bold mb-4 text-center">비밀번호 찾기</h2>
                <form className="mt-8 space-y-4" onSubmit={handleSubmit(onSubmit)}>
                    <input
                        {...register('loginId')}
                        type="text"
                        placeholder="아이디를 입력하세요"
                        className="w-full h-[60px] px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {errors.loginId && (
                        <p className="mt-1 text-sm text-red-500">{errors.loginId.message}</p>
                    )}

                    <input
                        {...register('name')}
                        type="text"
                        placeholder="이름을 입력하세요"
                        className="w-full h-[60px] px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {errors.name && (
                        <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>
                    )}

                    <div className="flex items-center space-x-2 mb-4">
                        <input
                            {...register('phoneNumber')}
                            type="tel"
                            placeholder="전화번호를 입력하세요"
                            className="w-full h-[60px] px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                            type="button"
                            className="px-4 h-[60px] py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 whitespace-nowrap"
                            onClick={handlePhoneVerification}
                            disabled={verificationStatus === 'sending' || !loginId || !phoneNumber || !name}
                        >
                            {verificationStatus === 'idle' || remainingTime === 0
                                ? '인증요청'
                                : verificationStatus === 'sending'
                                    ? '요청중...'
                                    : '재요청'}
                        </button>
                    </div>
                    {errors.phoneNumber && (
                        <p className="mt-1 text-sm text-red-500">{errors.phoneNumber.message}</p>
                    )}

                    {verificationMessage && (
                        <p className={`mt-2 text-sm ${
                            verificationMessage.includes('발송되었습니다')
                                ? 'text-green-500'
                                : 'text-red-500'
                        }`}>
                            {verificationMessage}
                        </p>
                    )}

                    {verificationStatus === 'sent' && remainingTime > 0 && (
                        <p className="mt-1 text-sm text-gray-500">
                            남은 시간: {formatTime(remainingTime)}
                        </p>
                    )}

                    {verificationStatus === 'sent' && (
                        <div>
                            <input
                                {...register('authNumber')}
                                type="text"
                                placeholder="인증번호 입력"
                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            {errors.authNumber && (
                                <p className="mt-1 text-sm text-red-500">{errors.authNumber.message}</p>
                            )}
                        </div>
                    )}

                    <button
                        type="submit"
                        className="w-full h-[60px] py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 mt-6 disabled:opacity-50"
                        disabled={isLoading || !authNumber || verificationStatus !== "sent"}
                    >
                        {isLoading ? "처리 중..." : "비밀번호 찾기"}
                    </button>
                </form>

                <div className="text-center mb-6">
                    <button
                        onClick={() => navigate("/find-id")}
                        style={{ color: '#727B8B' }}
                        className="text-blue-500 underline decoration-2 underline-offset-4 font-family-pretendard semi-bold"
                    >
                        아이디 찾기
                    </button>
                </div>
            </div>
            <div className="flex justify-center mt-[200px]">
                <img
                    src="/images/softcat_new_logo.png"
                    alt=" "
                    className="w-[180px] h-[50px]"
                />
            </div>
        </div>
    );
};

export default FindPwPage;

